
import React from 'react';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';

export interface PlaceCategoryDisplay {
  icon: React.ReactElement;
  label: string;
}

/** Google Places type → display label. Kept in sync with PlaceDisplayCard. */
export const PLACE_GOOGLE_TYPE_TO_LABEL: Record<string, string> = {
  museum: 'Culture',
  art_gallery: 'Culture',
  cultural_landmark: 'Culture',
  historical_landmark: 'Culture',
  monument: 'Culture',
  performing_arts_theater: 'Culture',
  opera_house: 'Culture',
  library: 'Culture',
  restaurant: 'Food',
  cafe: 'Food',
  bar: 'Food',
  bakery: 'Food',
  coffee_shop: 'Food',
  night_club: 'Nightlife',
  casino: 'Nightlife',
  park: 'Nature',
  national_park: 'Nature',
  botanical_garden: 'Nature',
  beach: 'Nature',
  zoo: 'Nature',
  aquarium: 'Nature',
};

export const PLACE_STAY_TYPES = new Set([
  'hotel',
  'lodging',
  'resort_hotel',
  'hostel',
  'bed_and_breakfast',
  'guest_house',
  'inn',
  'motel',
  'campground',
]);

interface CategoryInput {
  gt_category?: 'eat' | 'stay' | 'do';
  category_name?: string;
  google_types?: string[];
}

const STAY_CATEGORY_KEYWORDS = [
  'hotel',
  'lodging',
  'motel',
  'accommodation',
  'resort',
  'bed_and_breakfast',
  'hostel',
  'guest_house',
];

/**
 * Whether a place is somewhere to stay (hotel, hostel, …).
 * Google types only decide when GT hasn't categorised the place itself.
 */
export function isStayPlace(place: CategoryInput): boolean {
  const { gt_category, category_name, google_types } = place;
  if (gt_category === 'stay') return true;

  const name = category_name?.toLowerCase();
  if (name && (PLACE_STAY_TYPES.has(name) || STAY_CATEGORY_KEYWORDS.some((k) => name.includes(k)))) {
    return true;
  }

  return !gt_category && !!google_types?.some((t) => PLACE_STAY_TYPES.has(t));
}

/**
 * Derive a { icon, label } pair for a place's category pill.
 * Returns null when no meaningful category can be resolved.
 *
 * Used by PlaceDisplayCard and CombinedContentPlaceModal so both surfaces
 * always show identical icons and labels.
 */
export function getPlaceCategoryDisplay(place: CategoryInput): PlaceCategoryDisplay | null {
  const { gt_category, category_name, google_types } = place;

  const isStay = isStayPlace(place);

  let label: string | null = null;

  const fromGoogleType = google_types?.find(
    (t) => PLACE_GOOGLE_TYPE_TO_LABEL[t] || PLACE_STAY_TYPES.has(t),
  );
  if (fromGoogleType) {
    label =
      PLACE_GOOGLE_TYPE_TO_LABEL[fromGoogleType] ??
      (PLACE_STAY_TYPES.has(fromGoogleType) ? 'Stay' : fromGoogleType.replace(/_/g, ' '));
  }

  if (!label && isStay) label = 'Stay';
  if (!label && gt_category === 'eat') label = 'Eat';
  if (!label && gt_category === 'do') label = 'Do';
  if (!label && category_name) {
    label =
      PLACE_GOOGLE_TYPE_TO_LABEL[category_name] ??
      (PLACE_STAY_TYPES.has(category_name) ? 'Stay' : category_name.replace(/_/g, ' '));
  }

  if (!label) return null;

  if (label === 'Stay') return { icon: <HotelIcon sx={{ fontSize: 16 }} />, label };
  if (label === 'Food' || label === 'Eat')
    return { icon: <RestaurantIcon sx={{ fontSize: 16 }} />, label: label === 'Food' ? 'Food' : 'Eat' };
  return { icon: <LocalActivityIcon sx={{ fontSize: 16 }} />, label };
}
